import {
  DEFAULT_LOCALE,
  fromStrapiLocale,
  type Locale,
  toStrapiLocale,
} from "@lib/i18n/locale";
import { getLocalePrefix } from "@lib/routing";
import { STRAPI_REVALIDATE_SECONDS } from "@lib/strapi/constants";
import strapi, { StrapiQuery } from "@lib/strapi/rest/client";
import type { components } from "@lib/strapi/rest/schema";

type Schemas = components["schemas"];

export type PageSection = NonNullable<
  components["schemas"]["Page"]["contentSectionsV2"]
>[number];

export type HeroSection = Schemas["SectionsV2HeroComponent"];
export type BankLogoRowSection = Schemas["SectionsV2BankLogoRowComponent"];
export type StatRowSection = Schemas["SectionsV2StatRowComponent"];
export type ProcessColumnsSection =
  Schemas["SectionsV2ProcessColumnsComponent"];
export type JourneyRouterSection = Schemas["SectionsV2JourneyRouterComponent"];
export type ProductShowcaseSection =
  Schemas["SectionsV2ProductShowcaseComponent"];
export type FounderNoteSection = Schemas["SectionsV2FounderNoteComponent"];
export type ReviewPanelSection = Schemas["SectionsV2ReviewPanelComponent"];
export type RateChartSection = Schemas["SectionsV2RateChartComponent"];
export type FinalCtaSection = Schemas["SectionsV2FinalCtaComponent"];
export type FaqAccordionSection = Schemas["SectionsV2FaqAccordionComponent"];
export type LogoMarqueeSection = Schemas["SectionsV2LogoMarqueeComponent"];
export type BenefitColumnsSection =
  Schemas["SectionsV2BenefitColumnsComponent"];
export type FeatureRowsSection = Schemas["SectionsV2FeatureRowsComponent"];
export type CtaSplitSection = Schemas["SectionsV2CtaSplitComponent"];
export type TestimonialSection = Schemas["SectionsV2TestimonialComponent"];
export type ArticleGridSection = Schemas["SectionsV2ArticleGridComponent"];
export type TopArticlesSection = Schemas["SectionsV2TopArticlesComponent"];
export type GlossarySection = Schemas["SectionsV2GlossaryComponent"];
export type CtaBlock = Schemas["BlocksV2CtaComponent"];
export type ImageBlock = Schemas["BlocksV2ImageComponent"];
export type PageSeo = Schemas["SharedSeoComponent"];

export interface PageContent {
  sections: PageSection[];
  seo?: PageSeo;
  localizations?: {
    data?: Array<{ attributes?: PageRef }>;
  };
}

export interface PageRef {
  locale?: string;
  slug?: string;
  title?: string;
}

export const toPath = (page?: {
  data?: { attributes?: unknown } | null;
}): string | undefined => {
  const { slug, locale = null } =
    (page?.data?.attributes as PageRef | undefined) ?? {};

  if (!slug) {
    return undefined;
  }

  const prefix = getLocalePrefix(fromStrapiLocale(locale) ?? DEFAULT_LOCALE);
  return slug === "/" ? prefix || "/" : `${prefix}/${slug}`;
};

export const toHref = (cta?: {
  href?: string;
  page?: { data?: { attributes?: unknown } | null };
}) => cta?.href ?? toPath(cta?.page);

export const focalPoint = (image?: ImageBlock) =>
  `${image?.focalX ?? 50}% ${image?.focalY ?? 50}%`;

export const RATIOS: Record<string, number> = {
  ratio_16_9: 16 / 9,
  ratio_4_3: 4 / 3,
  ratio_1_1: 1,
  ratio_2_3: 2 / 3,
};

/**
 * The same ratios as classes rather than numbers. Written out because Tailwind emits only
 * what it can read in the source: an `aspect-[${ratio}]` built at runtime never reaches the
 * stylesheet, and the frame silently keeps whatever it had.
 */
const ASPECTS: Record<string, string> = {
  ratio_16_9: "aspect-[16/9]",
  ratio_4_3: "aspect-[4/3]",
  ratio_1_1: "aspect-square",
  ratio_2_3: "aspect-[2/3]",
};

/** The frame the editor chose. The fallback is the section's own default, not a global one. */
export const aspectOf = (image: ImageBlock | undefined, fallback: string) =>
  ASPECTS[image?.ratio ?? fallback] ?? ASPECTS[fallback]!;

const query = ({
  slug,
  locale,
  preview,
}: {
  slug: string;
  locale: Locale;
  preview: boolean;
}): StrapiQuery => ({
  filters: { slug: { $eq: slug || "/" } },
  locale: toStrapiLocale(locale),
  publicationState: preview ? "preview" : "live",
  fields: "renderer",
  populate: {
    localizations: {
      fields: ["locale", "slug"],
      publicationState: preview ? "preview" : "live",
    },
    seo: "*",
    contentSectionsV2: {
      on: {
        "sections-v2.hero": {
          populate: {
            image: { populate: "media" },
            primaryCta: { populate: "page" },
            secondaryCta: { populate: "page" },
            promises: "*",
            trust: { populate: "image" },
          },
        },
        "sections-v2.bank-logo-row": {
          populate: { logos: { populate: "image" } },
        },
        "sections-v2.stat-row": { populate: "stats" },
        "sections-v2.process-columns": { populate: "steps" },
        "sections-v2.journey-router": {
          populate: { cards: { populate: "page" } },
        },
        "sections-v2.product-showcase": {
          populate: {
            points: "*",
            image: { populate: "media" },
            cta: { populate: "page" },
          },
        },
        "sections-v2.founder-note": {
          populate: { people: "*", image: { populate: "media" } },
        },
        "sections-v2.review-panel": { populate: "*" },
        "sections-v2.rate-chart": {
          populate: {
            primaryCta: { populate: "page" },
            link: { populate: "page" },
          },
        },
        "sections-v2.final-cta": {
          populate: {
            trust: "*",
            primaryCta: { populate: "page" },
            secondaryCta: { populate: "page" },
          },
        },
        "sections-v2.faq-accordion": { populate: { faqs: "*" } },
        "sections-v2.logo-marquee": {
          populate: { partners: { populate: "logo" } },
        },
        "sections-v2.benefit-columns": { populate: "items" },
        "sections-v2.feature-rows": {
          populate: { items: { populate: { image: { populate: "media" } } } },
        },
        "sections-v2.cta-split": {
          populate: {
            cta: { populate: "page" },
            image: { populate: "media" },
            person: { populate: "photo" },
          },
        },
        "sections-v2.testimonial": {
          populate: { items: { populate: { image: { populate: "media" } } } },
        },
        "sections-v2.article-grid": { populate: "*" },
        "sections-v2.top-articles": { populate: "*" },
        "sections-v2.glossary": { populate: "*" },
      },
    },
  },
});

/**
 * The v2 page for a slug, or `null` when there is none.
 *
 * A transport or HTTP failure throws rather than resolving empty: the two are
 * indistinguishable downstream, and an unreachable Strapi that looks like a page with no
 * content gets cached as a 200 by `revalidate`.
 *
 * `anyRenderer` is for routes of their own, like the learning center: the middleware never
 * sends their paths to the Pages Router, so the legacy flag on the page decides nothing.
 */
const getPage = async ({
  slug,
  locale,
  preview,
  anyRenderer = false,
}: {
  slug: string;
  locale: Locale;
  preview: boolean;
  anyRenderer?: boolean;
}): Promise<PageContent | null> => {
  const { data, error } = await strapi.GET("/pages", {
    params: { query: query({ slug, locale, preview }) },
    next: { revalidate: STRAPI_REVALIDATE_SECONDS },
  });

  if (error) {
    throw new Error(
      `Strapi did not answer for "${slug || "/"}": ${JSON.stringify(error)}`,
    );
  }

  const page = (data?.data ?? []).find(
    (entry) => anyRenderer || entry.attributes?.renderer === "v2",
  );

  if (!page) {
    return null;
  }

  return {
    sections: (page.attributes?.contentSectionsV2 ?? []) as PageSection[],
    seo: page.attributes?.seo,
    localizations: page.attributes?.localizations,
  };
};

export default getPage;
